import PropTypes from 'prop-types'
import { useRef, useEffect } from 'react'
import { formatAssessmentReason } from '../utils/textFormatting'

const IdeaStepDetail = ({ step, stepMet, stepScore, stepCount, stepName, stepDescription, stepAnalysisTitle, stepGauge, stepScoreType, stepReasoning, expandedStage, stageKey, invertColors = false, currentIdea, toggleStage, stepStatus = 'completed', onRefineIdea, onArchiveIdea, archiving = false, onProgressToMarketResearch, skipping = false, history = [] }) => {
  const headerRef = useRef(null)

  // Scroll to keep header visible when expanding on mobile
  useEffect(() => {
    if (expandedStage && headerRef.current) {
      // Small delay to allow the DOM to update with expanded content
      setTimeout(() => {
        headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    }
  }, [expandedStage])

  const countRefinements = () => {
    if (!history || history.length === 0) {
      console.log('IdeaStepDetail: No history available for refinement count')
      return 0
    }
    
    console.log('IdeaStepDetail: History entries:', history.map(h => ({ 
      previous_status: h.previous_status, 
      new_status: h.new_status, 
      timestamp: h.timestamp 
    })))
    
    let count = 0
    for (let i = 0; i < history.length; i++) {
      if (history[i].previous_status === 'idea-refinement' && history[i].new_status === 'triage') {
        count++
      }
    }
    
    console.log('IdeaStepDetail: Found', count, 'completed refinement cycles (idea-refinement -> triage)')
    
    return count
  }

  const refinementCount = countRefinements()
  
  // Debug: Log whenever component renders for triage
  if (stageKey === 'triage') {
    console.log('IdeaStepDetail [TRIAGE]: stepStatus =', stepStatus, ', stepMet =', stepMet)
    console.log('IdeaStepDetail [TRIAGE]: Refinement count:', refinementCount)
    console.log('IdeaStepDetail [TRIAGE]: Current idea status:', currentIdea?.status)
    console.log('IdeaStepDetail [TRIAGE]: Has triage assessment:', !!currentIdea?.triage_assessment)
    console.log('IdeaStepDetail [TRIAGE]: Has refinement questions:', currentIdea?.triage_assessment?.refinement_questions?.length)
    console.log('IdeaStepDetail [TRIAGE]: Force-progress condition (completed && !stepMet):', stepStatus === 'completed' && !stepMet)
  }

  const isOffensiveIdea = (reasoning) => {
    if (!reasoning) return false
    const lowerReasoning = reasoning.toLowerCase()
    return lowerReasoning.includes('offensive') || 
           lowerReasoning.includes('harmful') || 
           lowerReasoning.includes('unethical') ||
           lowerReasoning.includes('inappropriate') ||
           lowerReasoning.includes('cannot support')
  }

  const getColorClass = (type, score) => {
    const lowScore = score < 5
    const medScore = score > 4 && score < 7

    if (invertColors) {
      // For saturation: low scores (1-5) are good (green), high scores (9-10) are bad (red)
      if (type === 'bg') {
        if (lowScore) return 'bg-green-500/20 border-green-500/30'
        if (medScore) return 'bg-yellow-500/20 border-yellow-500/30'
        return 'bg-red-500/20 border-red-500/30'
      }
      if (type === 'icon') {
        if (lowScore) return 'bg-green-500'
        if (medScore) return 'bg-yellow-500'
        return 'bg-red-500'
      }
      if (type === 'text') {
        if (lowScore) return 'text-green-400'
        if (medScore) return 'text-yellow-400'
        return 'text-red-400'
      }
      if (type === 'border') {
        if (lowScore) return 'border-green-200'
        if (medScore) return 'border-yellow-200'
        return 'border-red-200'
      }
      if (type === 'expandedBg') {
        if (lowScore) return 'bg-green-500/20 border border-green-500/30'
        if (medScore) return 'bg-yellow-500/20 border border-yellow-500/30'
        return 'bg-red-500/20 border border-red-500/30'
      }
      if (type === 'badge') {
        if (lowScore) return 'bg-green-500/20 text-green-400 border border-green-500/30'
        if (medScore) return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      }
    } else {
      // Normal logic: high scores are good (green), low scores are bad (red)
      if (type === 'bg') {
        if (lowScore) return 'bg-red-500/20 border-red-500/30'
        if (medScore) return 'bg-yellow-500/20 border-yellow-500/30'
        return 'bg-green-500/20 border-green-500/30'
      }
      if (type === 'icon') {
        if (lowScore) return 'bg-red-500'
        if (medScore) return 'bg-yellow-500'
        return 'bg-green-500'
      }
      if (type === 'text') {
        if (lowScore) return 'text-red-400'
        if (medScore) return 'text-yellow-400'
        return 'text-green-400'
      }
      if (type === 'border') {
        if (lowScore) return 'border-red-200'
        if (medScore) return 'border-yellow-200'
        return 'border-green-200'
      }
      if (type === 'expandedBg') {
        if (lowScore) return 'bg-red-500/20 border border-red-500/30'
        if (medScore) return 'bg-yellow-500/20 border border-yellow-500/30'
        return 'bg-green-500/20 border border-green-500/30'
      }
      if (type === 'badge') {
        if (lowScore) return 'bg-red-500/20 text-red-400 border border-red-500/30'
        if (medScore) return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      }
    }
  }

  // Helper function to get score label based on stage and score
  const getScoreLabel = (stageKey, stepScore, invertColors = false) => {
    const scoreThresholds = {
      opportunity: [
        { threshold: 7, label: 'Strong' },
        { threshold: 5, label: 'Average' },
        { threshold: 0, label: 'Weak' }
      ],
      marketOpportunity: [
        { threshold: 10, label: 'Exceptional' },
        { threshold: 7, label: 'Strong' },
        { threshold: 5, label: 'Moderate' },
        { threshold: 0, label: 'Poor' }
      ],
      inverted: [
        { threshold: 9, label: 'Very High' },
        { threshold: 7, label: 'High' },
        { threshold: 6, label: 'Medium' },
        { threshold: 0, label: 'Low' }
      ],
      default: [
        { threshold: 7, label: 'High' },
        { threshold: 5, label: 'Medium' },
        { threshold: 0, label: 'Low' }
      ]
    }

    const thresholds = scoreThresholds[stageKey] ||
                      (invertColors ? scoreThresholds.inverted : scoreThresholds.default)

    return thresholds.find(({ threshold }) => stepScore >= threshold)?.label || 'Low'
  }

  // Helper function to get badge score label
  const getBadgeScoreLabel = (stageKey, stepScore, invertColors = false) => {
    const badgeThresholds = {
      opportunity: [
        { threshold: 7, label: 'Strong' },
        { threshold: 5, label: 'Average' },
        { threshold: 0, label: 'Weak' }
      ],
      marketOpportunity: [
        { threshold: 10, label: 'Exceptional' },
        { threshold: 7, label: 'Strong' },
        { threshold: 5, label: 'Moderate' },
        { threshold: 0, label: 'Poor' }
      ],
      inverted: [
        { threshold: 9, label: 'Very High' },
        { threshold: 7, label: 'High' },
        { threshold: 6, label: 'Medium' },
        { threshold: 0, label: 'Low' }
      ],
      default: [
        { threshold: 8, label: 'High' },
        { threshold: 6, label: 'Medium' },
        { threshold: 0, label: 'Low' }
      ]
    }

    const thresholds = badgeThresholds[stageKey] ||
                      (invertColors ? badgeThresholds.inverted : badgeThresholds.default)

    return thresholds.find(({ threshold }) => stepScore >= threshold)?.label || 'Low'
  }

  // Helper function to get step status text
  const getStepStatusText = () => {
    if (stepStatus === 'completed' && stepMet) {
      if (stepScoreType === "Percentage") {
        return (
          <span className={styling.titleText}>
            {Math.round(stepScore * 10)}% {stepGauge}
          </span>
        )
      }
      const scoreLabel = getScoreLabel(stageKey, stepScore, invertColors)
      return (
        <span className={styling.titleText}>
          {stepScore}/10 - {scoreLabel} {stepGauge}
        </span>
      )
    }

    // Handle force-progressed steps (completed but no data) - show confidence if available
    if (stepStatus === 'completed' && !stepMet && stageKey === 'triage') {
      if (currentIdea?.triage_assessment?.confidence) {
        const confidence = Math.round(currentIdea.triage_assessment.confidence * 100)
        // Return without span wrapper - parent already has styling.titleText color
        return `${confidence}% confidence`
      }
      return (
        <span className="text-gray-400">
          Skipped
        </span>
      )
    }

    if (stepStatus === 'inProgress') return 'In Progress...'
    if (stepStatus === 'needsRefinement') return 'Needs Refinement'
    if (stepStatus === 'rejected') return 'Rejected'
    if (stepStatus === 'blocked') return 'Blocked'
    return 'Assessment Pending...'
  }

  // Helper function to render step icon based on status and score
  const renderStepIcon = () => {
    if (stepStatus === 'completed' && stepMet) {
      if (stageKey === "saturation") {
        // For saturation: low scores (green) show tick, high scores (yellow/red) show !
        return stepScore < 5 ? (
          <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <span className="text-gray-800 text-lg font-bold">!</span>
        )
      } else {
        // For other stages: high scores (green) show tick, low scores (yellow/red) show !
        return stepScore > 6 ? (
          <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <span className="text-gray-800 text-lg font-bold">!</span>
        )
      }
    }

    // Handle force-progressed/skipped steps - use confidence for RAG
    if (stepStatus === 'completed' && !stepMet && stageKey === 'triage') {
      // Use confidence score for icon: >0.6 (>60%) = tick, otherwise = exclamation
      const confidence = currentIdea?.triage_assessment?.confidence || 0
      return confidence > 0.6 ? (
        <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <span className="text-gray-800 text-lg font-bold">!</span>
      )
    }

    if (stepStatus === 'inProgress') {
      return (
        <div className="w-3 h-3">
          <svg className="w-3 h-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
            <path fill="currentColor" className="opacity-75" d="m12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8v-2z"></path>
          </svg>
        </div>
      )
    }

    if (stepStatus === 'needsRefinement') {
      return <span className="text-gray-800 text-lg font-bold">!</span>
    }

    if (stepStatus === 'rejected') {
      return (
        <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )
    }

    if (stepStatus === 'blocked') {
      return (
        <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      )
    }

    return <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
  }

  // Get styling based on step status
  const getStepStyling = () => {
    if (stepStatus === 'completed' && stepMet) {
      let borderColor, titleColor, iconColor
      
      if (invertColors) {
        // For saturation: lower is better
        if (stepScore < 5) {
          borderColor = 'border-green-400'
          titleColor = 'text-green-400'
          iconColor = 'bg-green-400'
        } else if (stepScore < 7) {
          borderColor = 'border-yellow-400'
          titleColor = 'text-yellow-400'
          iconColor = 'bg-yellow-400'
        } else {
          borderColor = 'border-red-400'
          titleColor = 'text-red-400'
          iconColor = 'bg-red-400'
        }
      } else {
        // Normal: higher is better
        if (stepScore > 6) {
          borderColor = 'border-green-400'
          titleColor = 'text-green-400'
          iconColor = 'bg-green-400'
        } else if (stepScore > 4) {
          borderColor = 'border-yellow-400'
          titleColor = 'text-yellow-400'
          iconColor = 'bg-yellow-400'
        } else {
          borderColor = 'border-red-400'
          titleColor = 'text-red-400'
          iconColor = 'bg-red-400'
        }
      }
      
      return {
        container: `bg-gray-800/50 ${borderColor} shadow-sm hover:shadow-md`,
        icon: iconColor,
        titleText: titleColor,
        statusText: 'text-white'
      }
    } else if (stepStatus === 'completed' && !stepMet && stageKey === 'triage') {
      // Skipped triage - RAG based on confidence (matches other steps: >6/10 green, >4/10 yellow)
      const confidence = currentIdea?.triage_assessment?.confidence || 0
      let borderColor, titleColor, iconColor
      
      // Match existing RAG thresholds: >0.6 green, >0.4 yellow, else red
      if (confidence > 0.6) {
        borderColor = 'border-green-400'
        titleColor = 'text-green-400'
        iconColor = 'bg-green-400'
      } else if (confidence > 0.4) {
        borderColor = 'border-yellow-400'
        titleColor = 'text-yellow-400'
        iconColor = 'bg-yellow-400'
      } else {
        borderColor = 'border-red-400'
        titleColor = 'text-red-400'
        iconColor = 'bg-red-400'
      }
      
      return {
        container: `bg-gray-800/50 ${borderColor} shadow-sm`,
        icon: iconColor,
        titleText: titleColor,
        statusText: 'text-white'
      }
    } else if (stepStatus === 'inProgress') {
      return {
        container: 'bg-gray-800/50 border-blue-600 shadow-sm hover:shadow-md',
        icon: 'bg-blue-600',
        titleText: 'text-blue-600',
        statusText: 'text-white'
      }
    } else if (stepStatus === 'needsRefinement') {
      return {
        container: 'bg-gray-800/50 border-orange-500 shadow-sm hover:shadow-md',
        icon: 'bg-orange-500',
        titleText: 'text-orange-400',
        statusText: 'text-white'
      }
    } else if (stepStatus === 'rejected') {
      // CRITICAL: Red styling for rejected ideas (step 1)
      return {
        container: 'bg-gray-800/50 border-red-500 shadow-sm hover:shadow-md cursor-pointer',
        icon: 'bg-red-500',
        titleText: 'text-red-400',
        statusText: 'text-white'
      }
    } else if (stepStatus === 'blocked') {
      // Blocked steps (step 2-5 when idea is rejected)
      return {
        container: 'bg-gray-800/30 border-gray-600 opacity-40',
        icon: 'bg-gray-600',
        titleText: 'text-gray-500',
        statusText: 'text-gray-500'
      }
    } else {
      return {
        container: 'bg-gray-800/30 border-gray-600 opacity-60',
        icon: 'bg-gray-600',
        titleText: 'text-gray-400',
        statusText: 'text-gray-400'
      }
    }
  }

  const styling = getStepStyling()

  return (
    <div className={`${expandedStage ? '' : 'mb-3'}`} ref={headerRef}>
        <div className={`flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden ${styling.container} ${
                (stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected' ? 'cursor-pointer' : 'cursor-default'
             }`}
             onClick={(stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected' ? () => toggleStage(stageKey) : undefined}
             onKeyDown={(stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected' ? (e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                 e.preventDefault()
                 toggleStage(stageKey)
               }
             } : undefined}
             role={(stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected' ? 'button' : undefined}
             tabIndex={(stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected' ? 0 : -1}
             aria-expanded={expandedStage}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 ${styling.icon}`}>
          {renderStepIcon()}
        </div>
        <div className="flex-1 relative z-10">
            <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">
            {stepName}
            </h4>
            {stepDescription && (
              <p className="text-sm text-gray-400 mb-1">{stepDescription}</p>
            )}
            <p className={`text-base ${styling.titleText}`}>
              {getStepStatusText()}
            </p>
        </div>
        {((stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected') && (
            <span className="text-sm text-cyan-400 flex items-center gap-1 whitespace-nowrap ml-2 relative z-10">
              {expandedStage ? 'Hide' : 'Details'}
              <svg className={`w-4 h-4 transition-transform duration-200 ${expandedStage ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
        )}
        </div>

        {/* Expandable Content - For completed steps, needs refinement, force-progressed triage, and rejected ideas */}
        {expandedStage && ((stepStatus === 'completed' && stepMet) || stepStatus === 'needsRefinement' || (stepStatus === 'completed' && !stepMet && stageKey === 'triage') || stepStatus === 'rejected') && (
        <div className="p-4 rounded-lg shadow-sm space-y-4 bg-gray-800/50 border border-gray-700/50">
        {/* Confidence Bar - Only show for completed steps */}
        {stepStatus === 'completed' && stepMet && (
          <div>
              <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-300">{stepGauge}</span>
              <div className={`px-2 py-1 rounded-full text-sm font-medium ${styling.titleText} bg-gray-700/50`}>
                {getBadgeScoreLabel(stageKey, stepScore, invertColors)}
              </div>
              </div>
              <div className="bg-gray-600 rounded-full h-2 overflow-hidden">
              <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${stepScore*10}%` }}
              ></div>
              </div>
          </div>
        )}

        {/* Force-progressed Triage Assessment - Show confidence and reasoning */}
        {stepStatus === 'completed' && !stepMet && currentIdea?.triage_assessment && (
          <div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">AI Confidence</span>
                <div className={`px-2 py-1 rounded-full text-sm font-medium bg-gray-700/50 ${styling.titleText}`}>
                  {Math.round(currentIdea.triage_assessment.confidence * 100)}%
                </div>
              </div>
              <div className="bg-gray-600 rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    currentIdea.triage_assessment.confidence > 0.6 
                      ? 'bg-gradient-to-r from-green-500 to-green-600' 
                      : currentIdea.triage_assessment.confidence > 0.4
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                      : 'bg-gradient-to-r from-red-500 to-red-600'
                  }`}
                  style={{ width: `${currentIdea.triage_assessment.confidence * 100}%` }}
                ></div>
              </div>
            </div>
            {currentIdea.triage_assessment.reasoning && (
              <div>
                {(() => {
                  const reasoning = currentIdea.triage_assessment.reasoning
                  console.log('IdeaStepDetail: Checking skip message regex')
                  console.log('IdeaStepDetail: Reasoning text:', reasoning)
                  console.log('IdeaStepDetail: First 100 chars:', reasoning.substring(0, 100))
                  
                  const skipMatch = reasoning.match(/^(You chose to skip (?:the \d+(?:st|nd|rd|th) )?refinement and progress to market research\.)\s*(.*)$/s)
                  console.log('IdeaStepDetail: Skip match result:', skipMatch ? 'MATCHED' : 'NO MATCH')
                  
                  if (skipMatch) {
                    console.log('IdeaStepDetail: Match group 1:', skipMatch[1])
                    console.log('IdeaStepDetail: Match group 2 (first 100 chars):', skipMatch[2]?.substring(0, 100))
                    
                    // Extract the main feedback and split at "To make this even stronger"
                    let mainFeedback = skipMatch[2]
                    let enhancementText = ''
                    const enhanceMatch = mainFeedback.match(/^(.*?)(To make this even stronger.*)$/is)
                    
                    if (enhanceMatch) {
                      mainFeedback = enhanceMatch[1].trim()
                      enhancementText = enhanceMatch[2].trim()
                    }
                    
                    return (
                      <div>
                        <p className="text-sm text-blue-400 mb-3 italic">{skipMatch[1]}</p>
                        <h5 className="text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wide">AI Reasoning</h5>
                        <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                          <div className="text-gray-300 text-base space-y-3">
                            <p className="leading-relaxed">{mainFeedback}</p>
                            {enhancementText && (
                              <p className="leading-relaxed">
                                Although your idea has moved to Market Research, it can be made even stronger by refining it further. {enhancementText}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }
                  
                  return (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wide">AI Reasoning</h5>
                      <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                        <div className="text-gray-300 text-base">
                          {formatAssessmentReason(reasoning)}
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
            
            {/* Add refine button for force-progressed ideas */}
            {currentIdea.triage_assessment.refinement_questions && currentIdea.triage_assessment.refinement_questions.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onRefineIdea?.()
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                  </svg>
                  <span>Refine your idea together</span>
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  Still have refinement questions available - strengthen your idea further
                </p>
              </div>
            )}
          </div>
        )}

        {/* Rejected Idea Feedback - Show AI reasoning and action buttons */}
        {stepStatus === 'rejected' && (
          <div>
            {currentIdea?.triage_assessment?.reasoning ? (
              <div className="mb-4">
                <h5 className="text-base font-semibold text-red-400 mb-2">AI Feedback</h5>
                <div className="bg-gray-700/50 rounded-xl p-4 border border-red-500/30">
                  <p className="text-base text-gray-300">{currentIdea.triage_assessment.reasoning}</p>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <h5 className="text-base font-semibold text-red-400 mb-2">Idea Rejected</h5>
                <div className="bg-gray-700/50 rounded-xl p-4 border border-red-500/30">
                  <p className="text-base text-gray-300">
                    This idea was rejected during triage. You can refine it to address the issues or archive it.
                  </p>
                </div>
              </div>
            )}
            
            {/* Action buttons for rejected ideas - only archive/unarchive */}
            <div className="space-y-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onArchiveIdea?.()
                }}
                disabled={archiving}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200 shadow-lg hover:shadow-xl font-medium ${archiving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {archiving ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 1 1 0-4h14a2 2 0 1 1 0 4M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-9 4h4" />
                  </svg>
                )}
                <span>{archiving ? 'Saving...' : currentIdea?.archived ? 'Unarchive This Idea' : 'Archive This Idea'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Refinement Requirements - Only show for needs refinement */}
        {stepStatus === 'needsRefinement' && (
          <div>
            {currentIdea?.triage_assessment?.refinement_questions && currentIdea.triage_assessment.refinement_questions.length > 0 ? (
              <>
                {currentIdea.triage_assessment.reasoning && (() => {
                  const reasoning = currentIdea.triage_assessment.reasoning
                  
                  // Parse structured feedback: extract summary and SWOT sections
                  const summaryMatch = reasoning.match(/\*\*SUMMARY:\*\*\s*([^*]+?)(?=\n\n|\*\*|$)/s)
                  const summary = summaryMatch ? summaryMatch[1].trim() : null
                  
                  // Extract SWOT sections
                  const strengthsMatch = reasoning.match(/\*\*🎯 Strengths:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const weaknessesMatch = reasoning.match(/\*\*⚠️ Weaknesses:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const opportunitiesMatch = reasoning.match(/\*\*🚀 Opportunities:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const threatsMatch = reasoning.match(/\*\*🛡️ Threats:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  
                  const hasStructuredFeedback = summary || strengthsMatch || weaknessesMatch
                  
                  if (hasStructuredFeedback) {
                    return (
                      <div className="mb-4">
                        <h5 className="text-base font-semibold text-orange-400 mb-3">My feedback</h5>
                        {refinementCount > 0 && (
                          <p className="text-sm text-gray-400 mb-3 italic">After {refinementCount} refinement{refinementCount > 1 ? 's' : ''}, your idea still needs some work.</p>
                        )}
                        
                        {/* Summary - visually distinctive */}
                        {summary && (
                          <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                            <p className="text-base text-blue-300 font-medium leading-relaxed">{summary}</p>
                          </div>
                        )}
                        
                        {/* SWOT Analysis */}
                        <div className="space-y-4">
                          {strengthsMatch && (
                            <div>
                              <h6 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                                <span>🎯</span> Strengths
                              </h6>
                              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                                <div className="text-gray-300 text-sm space-y-1" dangerouslySetInnerHTML={{ 
                                  __html: strengthsMatch[1].trim().split('\n').filter(line => line.trim()).map(line => 
                                    `<div class="flex items-start gap-2"><span class="text-green-400 mt-0.5">•</span><span>${line.trim().replace(/^-\s*/, '')}</span></div>`
                                  ).join('') 
                                }} />
                              </div>
                            </div>
                          )}
                          
                          {weaknessesMatch && (
                            <div>
                              <h6 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                                <span>⚠️</span> Weaknesses
                              </h6>
                              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                                <div className="text-gray-300 text-sm space-y-1" dangerouslySetInnerHTML={{ 
                                  __html: weaknessesMatch[1].trim().split('\n').filter(line => line.trim()).map(line => 
                                    `<div class="flex items-start gap-2"><span class="text-yellow-400 mt-0.5">•</span><span>${line.trim().replace(/^-\s*/, '')}</span></div>`
                                  ).join('') 
                                }} />
                              </div>
                            </div>
                          )}
                          
                          {opportunitiesMatch && (
                            <div>
                              <h6 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                                <span>🚀</span> Opportunities
                              </h6>
                              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                                <div className="text-gray-300 text-sm space-y-1" dangerouslySetInnerHTML={{ 
                                  __html: opportunitiesMatch[1].trim().split('\n').filter(line => line.trim()).map(line => 
                                    `<div class="flex items-start gap-2"><span class="text-blue-400 mt-0.5">•</span><span>${line.trim().replace(/^-\s*/, '')}</span></div>`
                                  ).join('') 
                                }} />
                              </div>
                            </div>
                          )}
                          
                          {threatsMatch && (
                            <div>
                              <h6 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                                <span>🛡️</span> Threats
                              </h6>
                              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                                <div className="text-gray-300 text-sm space-y-1" dangerouslySetInnerHTML={{ 
                                  __html: threatsMatch[1].trim().split('\n').filter(line => line.trim()).map(line => 
                                    `<div class="flex items-start gap-2"><span class="text-red-400 mt-0.5">•</span><span>${line.trim().replace(/^-\s*/, '')}</span></div>`
                                  ).join('') 
                                }} />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  }
                  
                  // Fallback for old format (non-structured feedback)
                  return (
                    <div className="mb-4">
                      <h5 className="text-base font-semibold text-orange-400 mb-2">My feedback</h5>
                      {refinementCount > 0 && (
                        <p className="text-sm text-gray-400 mb-2 italic">After {refinementCount} refinement{refinementCount > 1 ? 's' : ''}, your idea still needs some work.</p>
                      )}
                      <p className="text-base text-gray-300">{reasoning}</p>
                    </div>
                  )
                })()}
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onRefineIdea?.()
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl hover:from-orange-700 hover:to-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                  </svg>
                  <span>Refine your idea together</span>
                </button>
                
                <div className="space-y-2 mt-3">
                  {/* CRITICAL: Never show skip button for rejected ideas */}
                  {refinementCount >= 1 && 
                   currentIdea?.status !== 'rejected-offensive' && 
                   currentIdea?.status !== 'rejected-test-idea' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onProgressToMarketResearch?.()
                      }}
                      disabled={skipping}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 font-medium shadow-lg hover:shadow-xl ${skipping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {skipping ? (
                        <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      )}
                      <span>{skipping ? 'Progressing...' : 'Skip Further Refinement and Progress to Market Research'}</span>
                    </button>
                  )}
                  {(isOffensiveIdea(currentIdea?.triage_assessment?.reasoning) || refinementCount >= 1) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onArchiveIdea?.()
                      }}
                      disabled={archiving}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-gray-200 rounded-xl hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${archiving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {archiving ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 1 1 0-4h14a2 2 0 1 1 0 4M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-9 4h4" />
                        </svg>
                      )}
                      <span>{archiving ? 'Saving...' : isOffensiveIdea(currentIdea?.triage_assessment?.reasoning) ? "This doesn't seem viable - Archive it" : currentIdea?.archived ? 'Unarchive This Idea' : 'Archive This Idea'}</span>
                    </button>
                  )}
                  {!isOffensiveIdea(currentIdea?.triage_assessment?.reasoning) && refinementCount < 1 && (
                    <p className="text-xs text-gray-400 text-center">
                      I'll ask you targeted questions to help strengthen your idea
                    </p>
                  )}
                </div>
              </>
            ) : currentIdea?.triage_assessment?.areas_for_improvement ? (
              <>
                <h5 className="text-base font-semibold text-red-400 mb-3">Areas Requiring Improvement:</h5>
                <ul className="space-y-2">
                  {currentIdea.triage_assessment.areas_for_improvement.map((area) => (
                    <li key={area} className="flex items-start space-x-2">
                      <div className="w-4 h-4 bg-red-400 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <span className="text-base text-gray-300">{area}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        )}

        {/* Market Entry Difficulty - Saturation Stage Only */}
        {stageKey === "saturation" && (
          <div className="mb-4">
            <div className="p-3 bg-gray-700/50 rounded-lg border border-gray-600">
              <div className="flex items-center justify-between text-base">
                <span className="text-gray-300 font-medium">Market Entry Difficulty:</span>
                <span className={`font-semibold ${styling.titleText}`}>
                  {stepScore >= 9 ? 'Very High' : stepScore >= 7 ? 'High' : 'Low'}
                </span>
              </div>
              <div className="mt-2 flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={`saturation-difficulty-${i}`}
                    className={`h-2 flex-1 rounded-full ${
                      i < Math.ceil(stepScore / 2)
                        ? (stepScore < 6 ? 'bg-green-400' : stepScore < 8 ? 'bg-yellow-400' : 'bg-red-400')
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Differentiation Strength - USP Stage Only */}
        {stageKey === "opportunity" && (
          <div className="mb-4">
            <div className="p-3 bg-gray-700/50 rounded-lg border border-gray-600">
              <div className="flex items-center justify-between text-base">
                <span className="text-gray-300 font-medium">Differentiation Strength:</span>
                <span className={`font-semibold ${styling.titleText}`}>
                  {stepScore >= 7 ? 'Strong' : stepScore >= 5 ? 'Average' : 'Weak'}
                </span>
              </div>
              <div className="mt-2 flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={`opportunity-strength-${i}`}
                    className={`h-2 flex-1 rounded-full ${
                      i < Math.ceil(stepScore / 2)
                        ? (stepScore >= 7 ? 'bg-green-400' : stepScore >= 5 ? 'bg-yellow-400' : 'bg-red-400')
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI Reasoning - Hide when needs refinement, force-progressed triage, OR rejected */}
        {stepReasoning && stepStatus !== 'needsRefinement' && stepStatus !== 'rejected' && !(stepStatus === 'completed' && !stepMet && stageKey === 'triage') && (() => {
          const reasoning = stepReasoning
          
          // Parse structured feedback: extract summary and step-specific sections
          const summaryMatch = reasoning.match(/\*\*SUMMARY:\*\*\s*([^*]+?)(?=\n\n|\*\*|$)/s)
          const summary = summaryMatch ? summaryMatch[1].trim() : null
          
          // Extract sections - different names per step type
          // Market Demand: What's Promising / What's Uncertain / How to Validate
          const promisingMatch = reasoning.match(/\*\*(?:✨ )?What's Promising:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const uncertainMatch = reasoning.match(/\*\*(?:❓ )?What's Uncertain:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const validateMatch = reasoning.match(/\*\*(?:🔍 )?How to Validate:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          
          // Market Saturation: Opportunities in the Market / Competitive Pressures / Ways to Stand Out
          const opportunitiesMatch = reasoning.match(/\*\*(?:🌟 )?Opportunities in the Market:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const pressuresMatch = reasoning.match(/\*\*(?:⚔️ )?Competitive Pressures:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const standOutMatch = reasoning.match(/\*\*(?:💎 )?Ways to Stand Out:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          
          // USP: What Makes This Different / Where Differentiation is Weak / How to Strengthen Uniqueness
          const differentMatch = reasoning.match(/\*\*(?:💎 )?What Makes This Different:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const weakMatch = reasoning.match(/\*\*(?:❓ )?Where Differentiation is Weak:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const strengthenMatch = reasoning.match(/\*\*(?:✨ )?How to Strengthen Uniqueness:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          
          // Opportunity Assessment: Why This Could Work / What Could Hold It Back / Next Steps to Consider
          const whyWorkMatch = reasoning.match(/\*\*(?:🚀 )?Why This Could Work:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const holdBackMatch = reasoning.match(/\*\*(?:⚠️ )?What Could Hold It Back:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const nextStepsMatch = reasoning.match(/\*\*(?:🎯 )?Next Steps to Consider:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          
          // Legacy format fallback: Pros/Cons/Recommendations
          const prosMatch = reasoning.match(/\*\*(?:✅ )?Pros:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const consMatch = reasoning.match(/\*\*(?:⚠️ )?Cons:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          const recommendationsMatch = reasoning.match(/\*\*(?:💡 )?Recommendations:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
          
          // Helper to parse section content into summary and bullets
          const parseSection = (content) => {
            if (!content) return null
            const lines = content.trim().split('\n').filter(line => line.trim())
            const bullets = lines.filter(line => line.trim().startsWith('-'))
            const summaryLines = lines.filter(line => !line.trim().startsWith('-'))
            return {
              summary: summaryLines.join(' ').trim(),
              bullets: bullets.map(b => b.trim().replace(/^-\s*/, ''))
            }
          }
          
          // Determine which sections to use based on what was matched
          let section1, section2, section3
          let section1Title, section2Title, section3Title
          let section1Color, section2Color, section3Color
          let section1Emoji, section2Emoji, section3Emoji
          
          if (promisingMatch && uncertainMatch) {
            // Market Demand format
            section1 = parseSection(promisingMatch[1])
            section2 = parseSection(uncertainMatch[1])
            section3 = validateMatch ? parseSection(validateMatch[1]) : null
            section1Title = "What's Promising"
            section2Title = "What's Uncertain"
            section3Title = "How to Validate"
            section1Color = "green"
            section2Color = "yellow"
            section3Color = "blue"
            section1Emoji = "✨"
            section2Emoji = "❓"
            section3Emoji = "🔍"
          } else if (opportunitiesMatch && pressuresMatch) {
            // Market Saturation format
            section1 = parseSection(opportunitiesMatch[1])
            section2 = parseSection(pressuresMatch[1])
            section3 = standOutMatch ? parseSection(standOutMatch[1]) : null
            section1Title = "Opportunities in the Market"
            section2Title = "Competitive Pressures"
            section3Title = "Ways to Stand Out"
            section1Color = "green"
            section2Color = "yellow"
            section3Color = "blue"
            section1Emoji = "🌟"
            section2Emoji = "⚔️"
            section3Emoji = "💎"
          } else if (differentMatch && weakMatch) {
            // USP format
            section1 = parseSection(differentMatch[1])
            section2 = parseSection(weakMatch[1])
            section3 = strengthenMatch ? parseSection(strengthenMatch[1]) : null
            section1Title = "What Makes This Different"
            section2Title = "Where Differentiation is Weak"
            section3Title = "How to Strengthen Uniqueness"
            section1Color = "green"
            section2Color = "yellow"
            section3Color = "blue"
            section1Emoji = "💎"
            section2Emoji = "❓"
            section3Emoji = "✨"
          } else if (whyWorkMatch && holdBackMatch) {
            // Opportunity Assessment format
            section1 = parseSection(whyWorkMatch[1])
            section2 = parseSection(holdBackMatch[1])
            section3 = nextStepsMatch ? parseSection(nextStepsMatch[1]) : null
            section1Title = "Why This Could Work"
            section2Title = "What Could Hold It Back"
            section3Title = "Next Steps to Consider"
            section1Color = "green"
            section2Color = "yellow"
            section3Color = "blue"
            section1Emoji = "🚀"
            section2Emoji = "⚠️"
            section3Emoji = "🎯"
          } else {
            // Legacy Pros/Cons/Recommendations format
            section1 = prosMatch ? parseSection(prosMatch[1]) : null
            section2 = consMatch ? parseSection(consMatch[1]) : null
            section3 = recommendationsMatch ? parseSection(recommendationsMatch[1]) : null
            section1Title = "Pros"
            section2Title = "Cons"
            section3Title = "Recommendations"
            section1Color = "green"
            section2Color = "yellow"
            section3Color = "blue"
            section1Emoji = "✅"
            section2Emoji = "⚠️"
            section3Emoji = "💡"
          }
          
          const hasStructuredFeedback = summary || section1 || section2
          
          if (hasStructuredFeedback) {
            return (
              <div>
                <h5 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">{stepAnalysisTitle}</h5>
                
                {/* Summary - visually distinctive, shown first */}
                {summary && (
                  <p className="text-base text-gray-300 leading-relaxed mb-4">{summary}</p>
                )}
                
                {/* Step-specific sections - each in its own box */}
                <div className="space-y-3">
                  {section1 && (
                    <div className="rounded-lg p-4 border bg-gray-800/50 border-gray-700">
                      <h6 className={`text-base font-semibold text-${section1Color}-400 mb-2 flex items-center gap-2`}>
                        <span>{section1Emoji}</span> {section1Title}
                      </h6>
                      {section1.summary && (
                        <p className="text-gray-300 text-base mb-2">{section1.summary}</p>
                      )}
                      {section1.bullets.length > 0 && (
                        <details className="group">
                          <summary className="text-base text-gray-400 cursor-pointer hover:text-gray-300 transition-colors list-none flex items-center gap-1 mb-2">
                            <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                            Show details ({section1.bullets.length})
                          </summary>
                          <div className="pl-3 border-l border-gray-700">
                            <div className="text-gray-300 text-base space-y-1.5">
                              {section1.bullets.map((bullet, idx) => (
                                <div key={`s1-${idx}`} className="flex items-start gap-2">
                                  <span className={`text-${section1Color}-400/50 mt-0.5 flex-shrink-0 text-xs`}>•</span>
                                  <span>{bullet}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                  
                  {section2 && (
                    <div className="rounded-lg p-4 border bg-gray-800/50 border-gray-700">
                      <h6 className={`text-base font-semibold text-${section2Color}-400 mb-2 flex items-center gap-2`}>
                        <span>{section2Emoji}</span> {section2Title}
                      </h6>
                      {section2.summary && (
                        <p className="text-gray-300 text-base mb-2">{section2.summary}</p>
                      )}
                      {section2.bullets.length > 0 && (
                        <details className="group">
                          <summary className="text-base text-gray-400 cursor-pointer hover:text-gray-300 transition-colors list-none flex items-center gap-1 mb-2">
                            <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                            Show details ({section2.bullets.length})
                          </summary>
                          <div className="pl-3 border-l border-gray-700">
                            <div className="text-gray-300 text-base space-y-1.5">
                              {section2.bullets.map((bullet, idx) => (
                                <div key={`s2-${idx}`} className="flex items-start gap-2">
                                  <span className={`text-${section2Color}-400/50 mt-0.5 flex-shrink-0 text-xs`}>•</span>
                                  <span>{bullet}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                  
                  {section3 && (
                    <div className="rounded-lg p-4 border bg-gray-800/50 border-gray-700">
                      <h6 className={`text-base font-semibold text-${section3Color}-400 mb-2 flex items-center gap-2`}>
                        <span>{section3Emoji}</span> {section3Title}
                      </h6>
                      {section3.summary && (
                        <p className="text-gray-300 text-base mb-2">{section3.summary}</p>
                      )}
                      {section3.bullets.length > 0 && (
                        <details className="group">
                          <summary className="text-base text-gray-400 cursor-pointer hover:text-gray-300 transition-colors list-none flex items-center gap-1 mb-2">
                            <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                            Show details ({section3.bullets.length})
                          </summary>
                          <div className="pl-3 border-l border-gray-700">
                            <div className="text-gray-300 text-base space-y-1.5">
                              {section3.bullets.map((bullet, idx) => (
                                <div key={`s3-${idx}`} className="flex items-start gap-2">
                                  <span className={`text-${section3Color}-400/50 mt-0.5 flex-shrink-0 text-xs`}>•</span>
                                  <span>{bullet}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          }
          
          // Fallback for old format (non-structured feedback)
          return (
            <div>
              <h5 className="text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wide">{stepAnalysisTitle}</h5>
              <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                <div className="text-gray-300 text-base">
                  {formatAssessmentReason(reasoning, stepScore, invertColors)}
                </div>
              </div>
            </div>
          )
        })()}

        {/* Evaluation Timestamp */}
        {step?.timestamp && (
          <div className="text-sm text-gray-400 text-center pt-2 border-t border-gray-600">
            Evaluated on {new Date(step.timestamp).toLocaleDateString()} at {new Date(step.timestamp).toLocaleTimeString()}
          </div>
        )}
        </div>
        )}
    </div>
  )

}

IdeaStepDetail.propTypes = {
  currentIdea: PropTypes.object,
  expandedStage: PropTypes.bool,
  toggleStage: PropTypes.func,
  stepMet: PropTypes.object,
  stepStatus: PropTypes.oneOf(['completed', 'inProgress', 'pending', 'needsRefinement']),
  step: PropTypes.object,
  stepScore: PropTypes.number,
  stepCount: PropTypes.string,
  stepName: PropTypes.string,
  stepDescription: PropTypes.string,
  stepAnalysisTitle: PropTypes.string,
  stepGauge: PropTypes.string,
  stepScoreType: PropTypes.string,
  stepReasoning: PropTypes.string,
  stageKey: PropTypes.string,
  invertColors: PropTypes.bool,
  onRefineIdea: PropTypes.func,
  onArchiveIdea: PropTypes.func,
  archiving: PropTypes.bool,
  onProgressToMarketResearch: PropTypes.func,
  skipping: PropTypes.bool,
  history: PropTypes.arrayOf(PropTypes.shape({
    status: PropTypes.string,
    timestamp: PropTypes.string,
    reasoning: PropTypes.string
  }))
}

export default IdeaStepDetail